import dayjs from 'dayjs';
import { visibleCardsFor } from './dashboardVisibleCards';

/*
  ════════════════════════════════════════════════════════════════════════════
  cardConfig.js — the ONLY file you edit to add / tweak a dashboard widget.
  ════════════════════════════════════════════════════════════════════════════
  <DynamicCards moduleName="stickyNotes" /> looks the module up here, calls the
  common Auth endpoint (/dashboard/cards?moduleName=...), and renders the LIVE
  response with this config. Pass ONLY the moduleName from the page.
  (The page may also pass `title` to override the heading below.)

  Each entry:
    title    -> heading above the section (page `title` prop overrides it)
    variant  -> 'sticky'     : sticky-note cards (needs the *Key field map below)
                'counts'     : one stat card per number in the response object
                'submission' : "Total Client Submission" look — a header total
                               + one progress bar per number (totalKey/infoText)
                'list'       : stat cards from an array (uses titleKey/countKey/…)
                'metrics'    : plain label/value rows in one card (optional header
                               badge via `headerExtra`); pass an ARRAY as staticData
                               to switch it to one row per array item (`listRows`)
                'bars'       : full-width solid-colour rows, label left, value right
    request  -> how to call the gateway:
                  method 'GET' | 'POST'
                  params { limit, offset, type, … }  -> query string
                  body   { … }                       -> JSON body (POST only)
    listPath -> dot-path to the data inside the API response:
                  'data.data' -> array (sticky/list) ; 'data' -> counts object

  Field map (sticky/list): every key OPTIONAL, [a, b] joins two fields.
  'counts' needs no field map — it auto-labels each number in the response.

  ──────────────────────────────────────────────────────────────────────────
  CUSTOMISE EACH CARD (all optional, work on every variant)
  ──────────────────────────────────────────────────────────────────────────
  Every card has a NAME:
    • counts / submission -> the humanized label   ('inProgress' -> 'In Progress')
    • sticky              -> the value of `colorBy` (e.g. the `status` text)
    • list                -> the first titleKey value

  color    -> base color for ALL cards in the section:
                omit          -> plain (counts) / auto-cycle (sticky)
                'auto'        -> a different palette color per card
                'green'       -> that one fixed color for every card

  colorMap -> per-card color BY NAME (overrides `color`), case-insensitive:
                colorMap: { 'In Progress': 'purple', Hired: 'green', Rejected: 'red' }
              Submission bars accept a palette name OR a raw hex ('#0ea5e9').
              counts / submission: the NAME may be the RAW API key
              ('pendingSubmissionCount') OR the humanized label ('Pending Submission Count').

  labelMap -> (counts / submission) rename a card's displayed text. Key by the
              raw API name OR the humanized label:
                labelMap: { pendingSubmissionCount: 'Pending', submittedToClientCount: 'Submitted' }

  colorBy  -> (sticky) which API field the colorMap/hide names come from:
                colorBy: 'status'  +  colorMap: { Pending: 'peach', Done: 'green' }

  hide     -> drop specific cards. Each entry is EITHER:
                a name   (string)  -> hides the card with that name (case-insensitive;
                                      counts/submission match the raw key OR the label)
                an index (number)  -> hides the card at that 0-based position
                hide: ['Rejected', 0]

  ──────────────────────────────────────────────────────────────────────────
  ALIGNMENT & COUNT
  ──────────────────────────────────────────────────────────────────────────
  layout   -> 'COLSxROWS' — sets columns AND caps the count to cols*rows:
                '4x2' -> 4 across, max 8 cards   '3x3' -> 3 across, max 9
                '2x4' -> 2 across, max 8 cards
  columns  -> just the column count, no cap (e.g. columns: 3).
  maxCount -> just a hard cap on the number of cards, keeps current columns.
              (explicit columns/maxCount win over whatever `layout` implies.)
              Defaults: counts/list = 4 cols, sticky = 2 cols, submission = 1.

  Palette color names (defined in PALETTE / dynamicCard.css):
                blue green purple peach red gray
  (sticky + counts use these as CSS classes; submission uses them as bar colors.)

  EVERYTHING above is also overridable per-placement from the page:
    <DynamicCards moduleName="jobsCount" title="Jobs" layout="4x2" />
*/
export const MODULE_CONFIG = {
  stickyNotes: {
    title: "Sticky Notes",
    variant: "sticky",
    request: { method: "GET", params: { limit: 20, offset: 0 } },
    listPath: "data.data",
    // Where the sticky-note "View all" link navigates to (omit to hide it).
    maxCount: 4,
    viewAllUrl: "/viewall-sticky",
    // Dispatched (window.dispatchEvent(new Event(...))) by the add-note popup
    // on a successful save; ModuleCards listens for it and refetches, since
    // closing the popup returns to this same mounted component (no remount).
    refreshEvent: "sticky-notes:refresh",
    pillKey: "status",
    headingKey: "title",
    bodyKey: "notes",
    // Notes sit in a fixed-height card; the body is clamped to this many lines
    // with an ellipsis. Hovering a CLIPPED note shows the full text in a popover
    // (notes that fit show no popover). 2–6 supported (see dynamicCard.css).
    bodyLines: 3,
    footerLeftKey: "createdByName",
    footerRightKey: "createdAt",
    dateKey: "reminder.time",
    // Colour each note from its `status` value (falls back to the auto cycle
    // for any status not listed here). Edit the names to match your API.
    colorBy: "status",
    colorMap: {
      Note: "green",
      Tagged: "red",
    },
    color: "auto",
    // hide: ['Archived'],   // e.g. hide notes whose status is 'Archived'
  },

  jobsCount: {
    variant: "counts",
    dynamicCommonCount: {
      module: "jobs",
      keys: ["jobRecruitmentStatus", "jobStatus"],
    },
    listPath: "",
    layout: "4x2",
    groupSeparator: "",
    labelMap: {
      total: "Total Jobs",
      active: "Active Jobs",
      myAssignedCount: "My Jobs",
    },
    color: "#f1f3f8",
    show: visibleCardsFor("jobsCount"),
    hide: ["inactive", "submitted", "noSubmissions"],
  },

  // Rendered as the "Total Client Submission" card: one header total + a
  // horizontal progress bar per number in the response (ClientSubmissionCard look).
  submissionCount: {
    title: "Total Client Submission",
    tooltipText:
      "Includes all submissions that are pending and submitted to the client.",
    variant: "submission",
    dynamicCommonCount: {
      module: "submissions",
      keys: ["clientSubmissionStatus.clientSubmissionStatusText"],
    },
    listPath: "",
    // 'total' key is preserved in the flat object from the dashboard conversion.
    totalKey: "total",
    labelMap: {
      Selected: "Selected",
      "Submitted to client": "Submitted to Client",
      "Not Submitted": "Not Submitted",
      Approved: "Approved",
    },
    colorMap: {
      Selected: "rgb(234, 175, 12)",
      "Submitted to client": "green",
      "Not Submitted": "red",
      Approved: "#1d4ed8",
    },
    show: visibleCardsFor("submissionCount"),
  },

  leadAptitude: {
    title: "Aptitude Test Results",
    variant: "assessment",
    request: {
      method: "GET",
      params: {},
    },
    listPath: "data",
    totalKey: "totalScore",
    maxKey: "maximumScore",
    statusKey: "status",
    attemptDateKey: "attemptDate",
    timeTakenKey: "timeTaken",
    rankKey: "rank",
    labelMap: {
      logicalReasoning: "Logical Reasoning",
      quantitativeAptitude: "Quantitative Aptitude",
      verbalAbility: "Verbal Ability",
    },
    colorMap: {
      logicalReasoning: "#7c3aed",
      quantitativeAptitude: "#0891b2",
      verbalAbility: "#16a34a",
    },
    hide: ["status", "attemptDate", "timeTaken", "rank", "maximumScore"],
  },

  onboardingCount: {
    variant: "counts",
    dynamicCommonCount: {
      module: "onboarding",
      keys: ["onboardingObStage"],
    },
    listPath: "",
    frame: true,
    frameTitle: "Onboarding",
    frameClassName: "onboarding-card",
    viewAll: true,
    footer: true,
    columns: 4,
    valueMap: {
      inProgress: "Under Review",
      hired: "Onboarded",
      projectCompleted: "Project cycle closed",
      archive: "No longer active",
    },
    labelMap: {
      inProgress: "In Progress",
      hired: "Hired",
      projectCompleted: "Project Completed",
      archive: "Archive",
    },
    aggregateMap: {
      inProgress: ["inProgress", "In Progress"],
      hired: ["hired", "Hired", "Active"],
      projectCompleted: ["projectCompleted", "Project Completed", "Completed"],
      archive: ["archive", "Archive", "Not Hired", "Candidate Backout"],
    },
    show: visibleCardsFor("onboardingCount"),
    hide: ["total", "totalGrossMargin", "totalNetMargin"],
  },

  // Timesheet review/calendar sidebar — driven off already-fetched detail data
  // via <DynamicCards staticData={...} />, not a live dashboard fetch. See the
  // 'metrics' / 'bars' variant docs above.
  timesheetMonthlySummary: {
    variant: "metrics",
    title: "Monthly Summary",
    valueSuffix: " hrs",
    rows: [
      { key: "unapprovedPayableHours", label: "Unapproved Payable hours", sublabel: "(S + O)" },
      { key: "nonPayableHours", label: "Non-payable hours", sublabel: "(Leave/Holidays)" },
      { key: "approvedPayableHours", label: "Approved Payable hours", valueColor: "#15803d" },
    ],
  },

  // Only rendered by the page when a `weeks` array exists (e.g. the resource's
  // own Timesheet Calendar page); pass `staticData={weeks}` directly (an array),
  // not the summary object — see the 'metrics' variant's list mode.
  timesheetWeeklyTotals: {
    variant: "metrics",
    title: "Weekly Totals (S + O)",
    plainHeader: true,
    valueSuffix: " hrs",
    listRows: {
      itemValueKey: "weeklyTotals.payable",
      itemLabel: (week, i) => {
        const start = week.weekStart ? dayjs(week.weekStart).format("MMM D") : "";
        const end = week.weekEnd ? dayjs(week.weekEnd).format("MMM D") : "";
        const label = `Week ${week.weekNumber ?? i + 1}`;
        return start && end ? `${label} (${start} - ${end})` : label;
      },
    },
  },

  timesheetHourBreakdown: {
    variant: "bars",
    valueSuffix: " hrs",
    rows: [
      { key: "totalSTHours", label: "Standard", color: "#1d4ed8", bg: "#D0E6FF" },
      { key: "totalOTHours", label: "Overtime", color: "#15803d", bg: "#d9f7e3" },
      { key: "leaveHours", label: "Leaves", color: "#b45309", bg: "#FFECBA" },
      { key: "holidayHours", label: "Holidays", color: "#6d28d9", bg: "#ede9fe", fallback: 0 },
    ],
  },

  // Finance (Billing + Payroll) KPI row. The page computes `items` itself
  // (icons, labels, formatted money, tones — same shape FinanceKpiRow's
  // `items` prop always took) and passes it straight through as staticData;
  // this config only picks the 'kpi' variant. One entry covers both routes
  // since FinanceListPage.jsx renders both Billing and Payroll.
  financeKpiRow: {
    variant: "kpi",
  },
};

// ── Colour palettes ─────────────────────────────────────────────────────────
// Names here must exist as classes in dynamicCard.css (.dyn-card-item.<name>
// and .sticky-note.<name>). Add a name here AND a class there to extend them.

// counts / list cards cycle through these when color:'auto'.
export const PALETTE = ["blue", "green", "purple", "peach", "red", "gray"];

// sticky notes cycle through these when color:'auto'.
export const STICKY_PALETTE = ["purple", "peach", "green"];

// submission bars cycle through these hex values when no colorMap matches.
export const BAR_PALETTE = [
  "#1d4ed8",
  "#ef4444",
  "#16a34a",
  "#9333ea",
  "#f59e0b",
  "#0891b2",
];

// Palette NAME -> hex, so colorMap palette names work for the inline submission bars.
export const NAMED_COLORS = {
  blue: "#1d4ed8",
  green: "#16a34a",
  purple: "#9333ea",
  peach: "#f97316",
  red: "#ef4444",
  gray: "#6b7280",
};

// Returns the effective config: built-in defaults deep-merged with whatever
// the host project injected via configureCoreTheme({ cardConfig: { … } }) or
// window.__CORE_THEME_CONFIG__.cardConfig. Called at render time so it always
// picks up the host's config even when set after import evaluation.
export function getModuleConfig() {
  const hostConfig =
    (typeof window !== 'undefined' && window.__CORE_THEME_CONFIG__?.cardConfig) ?? {};
  // Shallow merge per key: host entries WIN over built-ins, but built-ins remain
  // unless the host replaces them. To extend a built-in entry use spread:
  //   cardConfig: { jobsCount: { ...builtIn.jobsCount, title: 'Open Positions' } }
  return { ...MODULE_CONFIG, ...hostConfig };
}

// Built-in defaults for PeriodBadgeTag (Standard/Supplementary and friends) —
// same host-override contract as getModuleConfig() above, one level deeper
// (per config key, not per top-level field) since each entry is fully
// self-contained rather than a title/rows/variant bag.
const PERIOD_BADGE_CONFIG = {
  timesheet: {
    currentLabel: 'Standard',      currentColor: 'blue',
    pastLabel: 'Supplementary',    pastColor: 'gold',
    // Full calendar months back that still count as "current". 0 = only
    // this month; 1 = this month and last month both read as Standard.
    thresholdMonths: 0,
  },
};

// Effective period-badge config for `key` (e.g. 'timesheet'): built-in
// default merged with configureCoreTheme({ periodBadgeConfig: { [key]: {…} } })
// or window.__CORE_THEME_CONFIG__.periodBadgeConfig — host entries win,
// field by field, over the matching built-in.
export function getPeriodBadgeConfig(key) {
  const hostConfig =
    (typeof window !== 'undefined' && window.__CORE_THEME_CONFIG__?.periodBadgeConfig) ?? {};
  return { ...PERIOD_BADGE_CONFIG[key], ...hostConfig[key] };
}

export default MODULE_CONFIG;
